import { CloseIcon, WarningIconRed } from 'assests/GmsIcons';
import React, { useState, useEffect, useRef } from 'react';

type RenameIterationDialogProps = {
    isOpen: boolean;
    currentName: number;
    existingNames: number[];
    onSave: (newName: number) => void;
    onCancel: () => void;
};

const RenameIterationDialog: React.FC<RenameIterationDialogProps> = ({
    isOpen,
    currentName,
    existingNames,
    onSave,
    onCancel
}) => {
    const [inputValue, setInputValue] = useState<number>(currentName);
    const [validationError, setValidationError] = useState<string>('');
    const inputRef = useRef<HTMLInputElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);

    // Update input value when currentName changes
    useEffect(() => {
        setInputValue(currentName);
        setValidationError('');
    }, [currentName]);

    // Focus input when dialog opens
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isOpen]);

    // Validate input value
    const validateInput = (value: number): string => {
        if (!value || value <= 0) {
            return 'Please enter a valid iteration number';
        }
        if (value !== currentName && existingNames.includes(value)) {
            return 'Iteration with the same name already exists';
        }

        return '';
    };

    // Handle keyboard events
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!isOpen) return;

            if (event.key === 'Escape') {
                onCancel();
            } else if (event.key === 'Enter') {
                event.preventDefault();
                handleSave();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, inputValue, onCancel]);

    // Handle click outside to close
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (overlayRef.current && event.target === overlayRef.current) {
                onCancel();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onCancel]);

    const handleSave = () => {
        const error = validateInput(inputValue);

        if (error) {
            setValidationError(error);
            return;
        }

        setValidationError('');
        onSave(inputValue);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value) || 0;
        setInputValue(value);

        // Clear validation error when user starts typing
        if (validationError) {
            setValidationError('');
        }
    };

    const hasError = !!validationError;
    const isValidInput = inputValue > 0 && !hasError;

    if (!isOpen) return null;

    return (
        <div
            ref={overlayRef}
            className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-50"
            data-testid="rename-iteration-dialog-overlay"
        >
            <div className="tw-bg-white tw-shadow-lg tw-w-96 tw-relative" data-testid="rename-iteration-dialog">
                {/* Header */}
                <div className="tw-h-12 tw-relative tw-border-b tw-border-zinc-300">
                    <h2 className="tw-absolute tw-left-4 tw-top-3.5 tw-text-slate-800 tw-text-sm tw-font-semibold tw-leading-tight">
                        Rename Iteration
                    </h2>

                    {/* Close button */}
                    <button
                        onClick={onCancel}
                        className="tw-absolute tw-right-4 tw-top-3 tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center tw-text-gray-400 hover:tw-text-gray-600"
                        data-testid="close-dialog-button"
                    >
                        <CloseIcon />
                    </button>
                </div>

                {/* Content */}
                <div className="tw-px-6 tw-py-6">
                    <div className="tw-flex tw-flex-col tw-gap-4">
                        <div className="tw-relative">
                            <label
                                htmlFor="iteration-name-input"
                                className="tw-block tw-text-gray-400 tw-text-xs tw-font-semibold tw-leading-tight tw-mb-2"
                            >
                                Iteration Name
                            </label>

                            <div className="tw-relative">
                                <input
                                    ref={inputRef}
                                    id="iteration-name-input"
                                    type="text"
                                    inputMode="numeric"
                                    value={inputValue === 0 ? '' : inputValue.toString()}
                                    onChange={handleInputChange}
                                    className={`tw-w-full tw-pl-20 tw-px-3 tw-py-2 tw-bg-white tw-border tw-rounded tw-text-sm tw-leading-tight focus:tw-outline-none focus:tw-ring-1 ${hasError
                                        ? 'tw-border-orange-700 focus:tw-border-orange-700 focus:tw-ring-orange-700'
                                        : 'tw-border-gray-200 focus:tw-border-blue-500 focus:tw-ring-blue-500'
                                        }`}
                                    placeholder=""
                                    data-testid="iteration-name-input"
                                />
                                <div className="tw-absolute tw-inset-y-0 tw-left-3 tw-flex tw-items-center tw-pointer-events-none">
                                    <span className="tw-text-gray-400 tw-text-sm tw-font-normal tw-leading-tight">
                                        Iteration{' '}
                                    </span>
                                </div>
                            </div>

                            {/* Error message */}
                            {validationError && (
                                <div className="tw-flex tw-items-center tw-gap-2 tw-mt-2" data-testid="validation-error">
                                    <WarningIconRed />
                                    <span className="tw-justify-start tw-text-orange-700 tw-text-sm tw-font-normal">
                                        {validationError}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="tw-px-6 tw-pb-6 tw-flex tw-justify-end tw-gap-3">
                    <button
                        onClick={onCancel}
                        className="tw-px-5 tw-py-2 tw-bg-white tw-border tw-border-blue-800 tw-text-blue-800 tw-text-sm tw-font-normal tw-leading-tight tw-rounded hover:tw-bg-blue-50 tw-transition-colors"
                        data-testid="cancel-button"
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleSave}
                        disabled={!isValidInput}
                        className="tw-px-5 tw-py-2 tw-bg-blue-800 tw-text-white tw-text-sm tw-font-normal tw-leading-tight tw-rounded hover:tw-bg-blue-700 disabled:tw-bg-gray-400 disabled:tw-cursor-not-allowed tw-transition-colors"
                        data-testid="save-button"
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RenameIterationDialog;